import {
  get_utc_offset,
  create_utc_timestamp_that_appears_as_local_midnight
} from '#lib/util.js';
import { format_date as format_date_from_date_utils } from '#lib/date_utils.js';
import * as public_env from "$app/env/public";

const PUBLIC_WEEK_START = parseInt(public_env.PUBLIC_WEEK_START, 10);
const PUBLIC_WEEK_END = parseInt(public_env.PUBLIC_WEEK_END, 10);
const PUBLIC_DAY_START = parseInt(public_env.PUBLIC_DAY_START, 10);
const PUBLIC_DAY_END = parseInt(public_env.PUBLIC_DAY_END, 10);
const TIME_LOCALE = public_env.PUBLIC_TIME_LOCALE || 'en-DE';
const TIME_ZONE = public_env.PUBLIC_TIME_ZONE || 'Europe/Berlin';
const TIME_HOUR_FORMAT = public_env.PUBLIC_TIME_HOUR_FORMAT || '24';
const PUBLIC_BOOKING_WINDOW_SIZE_MEETING_ROOM = parseInt(public_env.PUBLIC_BOOKING_WINDOW_SIZE_MEETING_ROOM || 6, 10);
const PUBLIC_BOOKING_WINDOW_SIZE_FLEX_DESK = parseInt(public_env.PUBLIC_BOOKING_WINDOW_SIZE_FLEX_DESK || 3, 10);
const PUBLIC_BOOKING_WINDOW_SIZE_FIXED_DESK = parseInt(public_env.PUBLIC_BOOKING_WINDOW_SIZE_FIXED_DESK || 3, 10);
const PUBLIC_BOOKING_WINDOW_SIZE_OFFICE = parseInt(public_env.PUBLIC_BOOKING_WINDOW_SIZE_OFFICE || 3, 10);
const PUBLIC_BOOKING_WINDOW_SIZE_ADDON = parseInt(public_env.PUBLIC_BOOKING_WINDOW_SIZE_ADDON || 3, 10);
const PUBLIC_BOOKING_WINDOW_SIZE_OPEN_DAY = parseInt(public_env.PUBLIC_BOOKING_WINDOW_SIZE_OPEN_DAY || 3, 10);
const PUBLIC_CANCELLATION_NOTICE_PERIOD_OFFICE = parseInt(public_env.PUBLIC_CANCELLATION_NOTICE_PERIOD_OFFICE || 3, 10);
const PUBLIC_CANCELLATION_NOTICE_PERIOD_FIXED_DESK = parseInt(public_env.PUBLIC_CANCELLATION_NOTICE_PERIOD_FIXED_DESK || 3, 10);
const PUBLIC_CANCELLATION_NOTICE_PERIOD_ADDON = parseInt(public_env.PUBLIC_CANCELLATION_NOTICE_PERIOD_ADDON || 3, 10);
export const MIN_CANCELLATION_MINUTES_HALF_HOURLY_BOOKINGS = parseInt(public_env.PUBLIC_MIN_CANCELLATION_MINUTES_HALF_HOURLY_BOOKINGS || 15, 10);
const MIN_REMAINING_MINUTES_FOR_BOOKING_OF_HALF_HOURLY_SLOT = parseInt(public_env.PUBLIC_MIN_REMAINING_MINUTES_FOR_BOOKING_OF_HALF_HOURLY_SLOT || 15, 10);

/**
 * Formats a half-hourly booking date for patch-work notification emails.
 * @param {string} booking_date - Booking date in YYYY-MM-DD format.
 * @returns {string} Formatted booking date based on business locale/timezone.
 */
export function format_half_hourly_booking_date_for_email(booking_date) {
  const booking_date_local_midnight = create_utc_timestamp_that_appears_as_local_midnight(
    booking_date,
    TIME_ZONE
  );

  return format_date_from_date_utils(
    booking_date_local_midnight,
    'MMMMDEEEY',
    TIME_LOCALE,
    TIME_ZONE,
    TIME_HOUR_FORMAT
  );
}

export function get_week_dates(week, year) {
  // Create a date object for January 1st of the given year in UTC
  const januaryFirst = new Date(Date.UTC(year, 0, 1));
  
  // Calculate the first Thursday of the year (used to determine the first week)
  const firstThursday = new Date(Date.UTC(year, 0, 1 + ((11 - januaryFirst.getUTCDay()) % 7)));
  
  // Calculate the Monday of the desired week
  const startDate = new Date(firstThursday);
  startDate.setUTCDate(firstThursday.getUTCDate() + (week - 1) * 7 - 3);
  
  // Calculate the Sunday of the desired week
  const endDate = new Date(startDate);
  endDate.setUTCDate(startDate.getUTCDate() + 6);
  
  // Format dates to ISO strings (YYYY-MM-DD)
  const formatDate = (date) => date.toISOString().split('T')[0];
  
  return {
    start: formatDate(startDate),
    end: formatDate(endDate)
  };
}

/**
 * Returns the ISO-8601 week number and week-based year for a date.
 * ISO weeks start on Monday; week 1 is the week containing the first Thursday of the year.
 * Uses the same convention as get_week_dates, so for any date d:
 *   get_week_dates(get_iso_week_and_year(d).week, get_iso_week_and_year(d).year) contains d.
 * NOTE: around New Year the ISO year differs from the calendar year
 * (e.g. 2027-01-01 is week 53 of ISO year 2026) — always pair week with the returned year.
 *
 * @param {Date} date - Date (UTC or fake-UTC/business-localized) whose UTC calendar date is used
 * @returns {{ week: number, year: number }}
 */
export function get_iso_week_and_year(date) {
  const target = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  // Move to the Thursday of this ISO week (Mon=0 … Sun=6)
  target.setUTCDate(target.getUTCDate() + 3 - ((target.getUTCDay() + 6) % 7));
  const iso_year = target.getUTCFullYear();
  // January 4th is always in ISO week 1 — find its week's Thursday
  const first_thursday = new Date(Date.UTC(iso_year, 0, 4));
  first_thursday.setUTCDate(first_thursday.getUTCDate() + 3 - ((first_thursday.getUTCDay() + 6) % 7));
  const week = 1 + Math.round((target.getTime() - first_thursday.getTime()) / (7 * 86400000));
  return { week, year: iso_year };
}

/**
 * Returns the number of ISO weeks (52 or 53) in a given ISO year.
 * December 28th is always in the last ISO week of its year.
 *
 * @param {number} year - The ISO year
 * @returns {number} 52 or 53
 */
export function get_iso_weeks_in_year(year) {
  return get_iso_week_and_year(new Date(Date.UTC(year, 11, 28))).week;
}

/**
 * Generates an array of date strings between two dates (inclusive)
 * @param {string} starts_on - Start date in YYYY-MM-DD format
 * @param {string} ends_on - End date in YYYY-MM-DD format
 * @returns {string[]} Array of date strings in YYYY-MM-DD format
 * @example
 * dates_from_range('2024-09-16', '2024-09-22')
 * Returns ['2024-09-16', '2024-09-17', '2024-09-18', '2024-09-19', '2024-09-20', '2024-09-21', '2024-09-22']
 */
export function dates_from_range(starts_on, ends_on) {
  const dates = [];
  // YYYY-MM-DD strings parse as UTC midnight; step with UTC methods so the
  // result is independent of the host timezone (local setDate would duplicate
  // a date across a DST spring-forward on a non-UTC host).
  let currentDate = new Date(starts_on);
  const endDate = new Date(ends_on);

  while (currentDate <= endDate) {
    dates.push(currentDate.toISOString().split('T')[0]);
    currentDate.setUTCDate(currentDate.getUTCDate() + 1);
  }
  return dates;
}

/**
 * Whether a value is a real calendar date in YYYY-MM-DD format. The format alone is not enough:
 * new Date('2026-09-31') is October 1st and SQLite reads it as October too, while code that
 * splits the string sees September, so an impossible date slips between both readings.
 * @param {unknown} value
 * @returns {boolean}
 */
export function is_valid_date_string(value) {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
}

/**
 * Formats a price from cents to a localized currency string
 * @param {number} price - Price in cents (must be an integer)
 * @param {string} [currency='EUR'] - Currency code
 * @param {string} [region_code='de'] - Region code
 * @param {string} [lang_code='de'] - Language code
 * @returns {string} Formatted price string or empty string if invalid
 */
export function format_price(price, {currency = 'EUR', locale = TIME_LOCALE ?? 'de-DE', decimal_digits = 0} = {}) {

  // console.log('format_price', price, currency, locale, decimal_digits);
  
  if (!Number.isInteger(price)) {
    console.error('format_price received a non-integer price', price);
    return '';
  }
  let options = {};
  if (currency) {
    options = {
      style: 'decimal',
      // TODO: refactor the 3x different format_price functions to util
      // Whole euros stay short (€60), cents always get two digits (€112,50, never €112,5)
      minimumFractionDigits: price % 100 === 0 ? decimal_digits : 2,
      maximumFractionDigits: 2
    }
  }
  const number_format = new Intl.NumberFormat(locale, options)
  return `€${number_format.format(price / 100)}`
}

/**
 * Gets the last day of a given month in UTC
 * @param {number|string} year - Full year (e.g., 2024)
 * @param {number|string} month - Month (1-12 or "01"-"12")
 * @returns {string} Last day of month in YYYY-MM-DD format
 * @example
 * get_last_day_of_month(2024, 2)  // Returns "2024-02-29"
 * get_last_day_of_month(2024, "02")  // Returns "2024-02-29"
 * get_last_day_of_month("2024", "09")  // Returns "2024-09-30"
 */
export function get_last_day_of_month(year, month) {
  const parsed_year = parseInt(year, 10);
  const parsed_month = parseInt(month, 10);
  // Create a UTC Date for the first day of the next month
  const last_day = new Date(Date.UTC(parsed_year, parsed_month, 0));
  // Format to YYYY-MM-DD
  return last_day.toISOString().split('T')[0];
}

export function get_first_day_of_month(year, month) {
  // Create a UTC Date for the first day of the specified month
  const first_day = new Date(Date.UTC(year, month - 1, 1));
  
  // Format to YYYY-MM-DD
  return first_day.toISOString().split('T')[0];
}


/**
 * Generates an array of time slots based on the given parameters.
 * @param {number} [slot_size_minutes=30] - The size of each time slot in minutes.
 * @param {boolean} [use24_hour_format=true] - Whether to use 24-hour format (true) or 12-hour format (false).
 * @returns {string[]} An array of time slots as formatted strings.
 * @example
 * Generate 30-minute slots in 24-hour format
 * generate_time_slots(30, true);
 * Returns: ['00:00', '00:30', '01:00', ..., '23:00', '23:30']
 */
export function generate_time_slots(slot_size_minutes = 30, use24_hour_format = true) {
  const slots_per_day = 24 * 60 / slot_size_minutes;
  
  return Array.from({ length: slots_per_day }, (_, index) => {
    const total_minutes = index * slot_size_minutes;
    const hours = Math.floor(total_minutes / 60);
    const minutes = total_minutes % 60;

    if (use24_hour_format) {
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    } else {
      const period = hours < 12 ? 'AM' : 'PM';
      const hours12 = hours % 12 || 12;
      return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
    }
  });
}


/**
 * Calculates the ISO week number (and its ISO week-based year) for the first
 * day of a given month.
 *
 * ISO-8601 definition of week number:
 * Week 1 is the week containing the first Thursday of the year
 * The first week of a year may include days from the previous year
 * The last week of a year may extend into the next year
 *
 * NOTE: for January the ISO year can be the previous calendar year
 * (e.g. 2027-01-01 is week 53 of ISO year 2026) — always pair the returned
 * week with the returned year when calling get_week_dates.
 *
 * @param {number} year - Full year (e.g., 2024)
 * @param {number} month - Month (0-11)
 * @returns {{ week: number, year: number }} ISO week number (1-53) and ISO week-based year
 */
export function get_week_number_for_start_of_month(year, month) {
  return get_iso_week_and_year(new Date(Date.UTC(year, month, 1)));
}

/**
 * Converts a date string and time slot number to a UTC Date object
 * @param {string} date_string - Date in YYYY-MM-DD format
 * @param {number} time_slot - Integer 0-47 representing half-hour increments
 * @returns {Date} UTC Date object
 * @example
 *   time_slot 16 = 08:00 (16/2 = 8 hours)
 *   time_slot 17 = 08:30 (17/2 = 8.5 hours)
 *   convert_time_slot_to_utc('2024-01-15', 16) // Returns UTC Date for 2024-01-15 08:00:00
 */
export function convert_time_slot_to_utc(date_string, time_slot) {
  const date = new Date(date_string);
  return new Date(Date.UTC(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    Math.floor(time_slot / 2),    // Convert time_slot to hours
    (time_slot % 2) * 30,         // Get remainder and multiply by 30 for minutes
    0                             // Seconds always 0
  ));
}


/**
 * Checks if a booking can be cancelled based on when it was created
 * @param {string} date_string - Date in YYYY-MM-DD format
 * @param {number} time_slot - Integer 0-47 representing half-hour increments
 * @param {string} booking_created_at - ISO timestamp when booking was created
 * @returns {boolean} True if booking can be cancelled, false otherwise
 */
export function can_cancel_half_hourly_booking(date_string, time_slot, booking_created_at) {
  const utc_offset_minutes = get_utc_offset();
  const MINUTE_IN_MS = 60000;
  const now_local = new Date(Date.now() + utc_offset_minutes * MINUTE_IN_MS);
  // Convert both times to UTC for comparison
  const now_utc = new Date(Date.now());
  const booking_created_utc = new Date(booking_created_at);
  const booking_age = now_utc - booking_created_utc;
  
  // If booking was made within the last minute, allow cancellation regardless of time
  if (booking_created_at && booking_age <= MINUTE_IN_MS) {
    return true;
  }

  const time_slot_utc = convert_time_slot_to_utc(date_string, time_slot);
  const time_until_slot_starts = time_slot_utc.getTime() - now_local.getTime();
  // Can't cancel if there's less than minimum cancellation time before slot starts
  const can_cancel = time_until_slot_starts >= MIN_CANCELLATION_MINUTES_HALF_HOURLY_BOOKINGS * MINUTE_IN_MS;
  
  return can_cancel;
}

/**
 * Checks if there's enough time left in a slot to make a booking worthwhile
 * @param {string} date_string - Date in YYYY-MM-DD format
 * @param {number} time_slot - Integer 0-47 representing half-hour increments
 * @returns {boolean} True if enough time left in slot, false otherwise
 */
export function half_hourly_slot_has_sufficient_time_remaining(date_string, time_slot) {
  const utc_offset_minutes = get_utc_offset(); // uses PUBLIC_TIME_ZONE specified in env.
  const MINUTE_IN_MS = 60000;
  const now_local = new Date(Date.now() + utc_offset_minutes * MINUTE_IN_MS);
  const time_slot_utc = convert_time_slot_to_utc(date_string, time_slot);
  
  // Calculate time remaining in current slot
  const time_until_slot_ends = time_slot_utc.getTime() + 30 * MINUTE_IN_MS - now_local.getTime();
  const minutes_remaining = time_until_slot_ends / MINUTE_IN_MS;
    
  return minutes_remaining >= MIN_REMAINING_MINUTES_FOR_BOOKING_OF_HALF_HOURLY_SLOT;
}

// TODO: doesn't seem to be used anywhere. Remove!?
/**
 * Determines the time state of a half-hourly booking slot
 * @param {Date} slot_date - The date of the slot
 * @param {number} slot_index - The index of the time slot
 * @returns {'past' | 'present' | 'future'}
 */
export function get_half_hourly_slot_time_state(slot_date, slot_index) {
  const now = new Date();
  const slot_start = new Date(slot_date);
  slot_start.setUTCHours(Math.floor(slot_index / 2));
  slot_start.setUTCMinutes((slot_index % 2) * 30);
  
  const slot_end = new Date(slot_start);
  slot_end.setUTCMinutes(slot_start.getUTCMinutes() + 30);

  if (now > slot_end) {
    return 'past';
  }
  if (now >= slot_start && now < slot_end) {
    return 'present';
  }
  return 'future';
}

/**
 * Checks if a given slot is within business hours
 * @param {number} week_day - Day of the week (0-6, where 0 is Sunday)
 * @param {number} slot_index - Half-hour slot index (0-47, where 0 is 00:00 and 47 is 23:30)
 * @returns {boolean} True if slot is within business hours, false otherwise
 * @example
 * For Monday at 9:30 AM (slot index 19)
 *   is_half_hourly_slot_inside_business_hours(1, 19) // => true
 * For Sunday at 14:00 (slot index 28)
 *   is_half_hourly_slot_inside_business_hours(0, 28) // => false
 */
export function is_half_hourly_slot_inside_business_hours(week_day, slot_index) {
  return (
    week_day >= PUBLIC_WEEK_START && 
    week_day <= PUBLIC_WEEK_END && 
    slot_index >= PUBLIC_DAY_START && slot_index < PUBLIC_DAY_END
  );
}

/**
 * Determines the user state of a booking slot
 * @param {string} booked_by - User ID of the booking user
 * @param {string} current_user_id - ID of the current user
 * @param {Array} delegators - List of users who delegated booking rights
 * @param {Array} representatives - List of users who can book on behalf
 * @returns {'me' | 'delegator' | 'representative' | 'other' | 'none'}
 */
export function get_slot_user_state(booked_by, current_user_id, delegators = [], representatives = []) {
  if (!booked_by) {
    return 'none';
  }
  if (booked_by === current_user_id) {
    return 'me';
  }
  if (delegators?.some(d => d.user_id === booked_by)) {
    return 'delegator';
  }
  if (representatives?.some(r => r.user_id === booked_by)) {
    return 'representative';
  }
  return 'other';
}

/**
 * Gets the booking window size for a resource type
 * @param {string} resource_type
 * @returns {number} Booking window size in months
 */
export function get_booking_window_size_for_resource_type(resource_type) {
  if (resource_type === 'meeting_room') return PUBLIC_BOOKING_WINDOW_SIZE_MEETING_ROOM;
  if (resource_type === 'flex_desk') return PUBLIC_BOOKING_WINDOW_SIZE_FLEX_DESK;
  if (resource_type === 'fixed_desk') return PUBLIC_BOOKING_WINDOW_SIZE_FIXED_DESK;
  if (resource_type === 'office') return PUBLIC_BOOKING_WINDOW_SIZE_OFFICE;
  if (resource_type === 'addon') return PUBLIC_BOOKING_WINDOW_SIZE_ADDON;
  if (resource_type === 'open_day') return PUBLIC_BOOKING_WINDOW_SIZE_OPEN_DAY;
}

/**
 * Gets the cancellation notice period for a resource type
 * @param {string} resource_type
 * @returns {number} Cancellation notice period in months
 */
export function get_cancellation_notice_period_for_resource_type(resource_type) {
  if (resource_type === 'fixed_desk') return PUBLIC_CANCELLATION_NOTICE_PERIOD_FIXED_DESK;
  if (resource_type === 'office') return PUBLIC_CANCELLATION_NOTICE_PERIOD_OFFICE;
  if (resource_type === 'addon') return PUBLIC_CANCELLATION_NOTICE_PERIOD_ADDON;
}


/**
 * Offsets the given month by a specified number of months using UTC for internal calculations.
 *
 * @param {number} year - The starting year in UTC.
 * @param {number} month - The starting month (1-12).
 * @param {number} offset - The offset in months to add (can be positive or negative).
 * @returns {[number, number]} An array containing the new year and month (1-indexed).
 *
 * @example
 * For example, offset_month(2024, 11, 3) returns [2025, 2]
 * const [new_year, new_month] = offset_month(2024, 11, 3);
 */
export function offset_month(year, month, offset) {
  // Convert to 0-indexed month (JavaScript Date uses 0-11 for months)
  const date = new Date(Date.UTC(year, month - 1, 1));
  date.setUTCMonth(date.getUTCMonth() + offset);

  // Return the new year and month (converting the month back to 1-indexed).
  return [date.getUTCFullYear(), date.getUTCMonth() + 1];
}


/**
 * Calculates the number of months between two dates specified by year and month
 * @param {number} year1 - First year
 * @param {number} month1 - First month (1-12)
 * @param {number} year2 - Second year 
 * @param {number} month2 - Second month (1-12)
 * @returns {number} Number of months between the two dates
 * @example
 * get_month_distance(2024, 1, 2025, 2) // Returns 13
 * get_month_distance(2024, 12, 2025, 1) // Returns 1
 * get_month_distance(2024, 1, 2024, 12) // Returns 11
 */
export function get_month_distance(year1, month1, year2, month2) {
  return (year2 - year1) * 12 + (month2 - month1);
}

/**
 * Hands out a member's monthly meeting room credit over the month's booked days, all rooms
 * together. Credit already on an invoice (billed days) is spent first, whatever the room or date;
 * what is left goes to the unbilled days in date order, so a later booking can never take credit
 * that an earlier day needs.
 * @param {Array<{ booking_date: string, resource_id: number, amount: number, billed: boolean, billed_credits_used?: number }>} days
 * @param {number} allowance - the month's credit in cents
 * @returns {number[]} credits used per day, in the order of `days`
 */
export function allocate_meeting_room_credits(days, allowance) {
  const billed_credits = days.reduce((sum, day) => sum + (day.billed ? day.billed_credits_used ?? 0 : 0), 0);
  let available = Math.max(allowance - billed_credits, 0);
  const credits = days.map(day => (day.billed ? day.billed_credits_used ?? 0 : 0));
  const unbilled = days
    .map((day, index) => ({ day, index }))
    .filter(({ day }) => !day.billed)
    .sort((a, b) => (a.day.booking_date < b.day.booking_date ? -1 : a.day.booking_date > b.day.booking_date ? 1 : a.day.resource_id - b.day.resource_id));
  for (const { day, index } of unbilled) {
    credits[index] = Math.min(available, day.amount);
    available -= credits[index];
  }
  return credits;
}

/** Defines pricing field requirements for each resource type */
export function is_pricing_valid_for_resource_type(pricing = {}, type) {
  const rules = {
    meeting_room: {
      fields: ['price', 'max_price'],
      validate: ({ price, max_price }) => price >= 0 && max_price >= price
    },
    flex_desk: {
      fields: ['price', 'max_price', 'available_quantity'],
      validate: ({ price, max_price, available_quantity }) => 
        price >= 0 && max_price >= price && available_quantity >= 0
    },
    office: {
      fields: ['price', 'available_quantity', 'meeting_room_credit_allowance', 'meeting_room_discount_percentage'],
      validate: ({ price, available_quantity, meeting_room_credit_allowance, meeting_room_discount_percentage }) => 
        price >= 0 && available_quantity >= 0 && meeting_room_credit_allowance >= 0 && meeting_room_discount_percentage >= 0
    },
    fixed_desk: {
      fields: ['price', 'available_quantity', 'meeting_room_credit_allowance', 'meeting_room_discount_percentage'],
      validate: ({ price, available_quantity, meeting_room_credit_allowance, meeting_room_discount_percentage}) => 
        price >= 0 && available_quantity >= 0 && meeting_room_credit_allowance >= 0 && meeting_room_discount_percentage >= 0
    },
    addon: {
      fields: ['price', 'available_quantity'],
      validate: ({ price, available_quantity}) => 
        price >= 0 && available_quantity >= 0
    },
    open_day: {
      fields: ['price', 'available_quantity'],
      validate: ({ price, available_quantity}) => 
        price >= 0 && available_quantity >= 0
    }
  }[type];

  return rules && 
    rules.fields.every(f => f in pricing) && 
    rules.validate(pricing);
}



/**
 * Svelte Action for swipe based forward/ backward navigation
 * @param {HTMLElement} node - The DOM node to attach swipe navigation to
 * @param {{on_navigate: (direction: 'forward' | 'back') => void}} options - Navigation options
 */
export function swipe_navigation(node, { on_navigate }) {
  const SWIPE_THRESHOLD = 150;
  const WHEEL_THRESHOLD = 80;
  const NAVIGATION_COOLDOWN = 500;

  let start_x = 0;
  let start_y = 0;
  let is_dragging = false;
  let last_navigation_time = 0;

  function handle_touch_start(event) {
    start_x = event.touches[0].clientX;
    start_y = event.touches[0].clientY;
    is_dragging = true;
  }

  function handle_touch_move(event) {
    if (!is_dragging) return;
    const current_x = event.touches[0].clientX;
    const current_y = event.touches[0].clientY;
    const diff_x = start_x - current_x;
    const diff_y = Math.abs(current_y - start_y);
    const currentTime = Date.now();

    if (Math.abs(diff_y) > Math.abs(diff_x)) return;

    if (Math.abs(diff_x) > SWIPE_THRESHOLD && currentTime - last_navigation_time > NAVIGATION_COOLDOWN) {
      on_navigate(diff_x > 0 ? 'forward' : 'back');
      is_dragging = false;
      last_navigation_time = currentTime;
      start_x = current_x;
    }
  }

  function handle_touch_end() {
    is_dragging = false;
  }

  function handle_wheel(event) {
    const currentTime = Date.now();
    if (Math.abs(event.deltaY) > Math.abs(event.deltaX)) return;

    if (Math.abs(event.deltaX) > WHEEL_THRESHOLD && currentTime - last_navigation_time > NAVIGATION_COOLDOWN) {
      on_navigate(event.deltaX > 0 ? 'forward' : 'back');
      last_navigation_time = currentTime;
    }
  }

  node.addEventListener('touchstart', handle_touch_start);
  node.addEventListener('touchmove', handle_touch_move);
  node.addEventListener('touchend', handle_touch_end);
  node.addEventListener('wheel', handle_wheel);

  return {
    destroy() {
      node.removeEventListener('touchstart', handle_touch_start);
      node.removeEventListener('touchmove', handle_touch_move);
      node.removeEventListener('touchend', handle_touch_end);
      node.removeEventListener('wheel', handle_wheel);
    }
  };
}


export function get_week_month_by_weekday_majority(year, week_number) {
  const { start: week_start, end: week_end } = get_week_dates(week_number, year);
  const dates = dates_from_range(week_start, week_end);
  const month_at_week_start = week_start.split('-')[1];
  const month_at_week_end = week_end.split('-')[1];

  if (month_at_week_start === month_at_week_end) {
    return parseInt(month_at_week_start, 10);
  }

  let month_at_week_start_count = 0;
  let month_at_week_end_count = 0;

  for (const date of dates) {
    const date_month = date.split('-')[1];
    if (date_month === month_at_week_start) {
      month_at_week_start_count++;
    } else {
      month_at_week_end_count++;
    }
  }

  return parseInt(month_at_week_start_count > month_at_week_end_count ? month_at_week_start : month_at_week_end, 10)
}


// This converts a local date (2025-01-28) + timeslot (0-47) to a "localized utc-based" date object
// NOTE: localized means, that the date object returned will
// Example: local_timeslot_as_localized_utc_date('2025-02-23', 16) => 2025-02-23T08:00:00.000Z
export function local_timeslot_as_localized_utc_date(local_date, local_timeslot) {
  // Calculate hours and minutes from timeslot
  const total_minutes = local_timeslot * 30;
  const hours = Math.floor(total_minutes / 60);
  const minutes = total_minutes % 60;
  
  // Create a new date object from the input date string
  const date = new Date(local_date); // e.g. local_date = '2025-01-28' => '2025-01-28T00:00:00.000Z'
  
  date.setUTCHours(hours);
  date.setUTCMinutes(minutes);
  date.setUTCSeconds(0);
  date.setUTCMilliseconds(0);

  return date;
}


/**
 * Extracts price information from either a resource or pricing object
 * @param {Object} obj - Either a resource object (with price, max_price, booking_type) or a pricing object (with price, max_price)
 * @param {string} [booking_type] - Required if obj is a pricing object, optional if obj is a resource object
 * @returns {{price: number, max_price: number, booking_type: string}} Normalized price info object
 */
export function extract_price_info(obj, booking_type) {
  return {
    price: obj.price ?? 0,
    max_price: obj.max_price ?? 0,
    booking_type: obj.booking_type ?? booking_type
  };
}

/**
 * Formats price information for display
 * @param {{
 *   price: number,
 *   max_price: number, 
 *   booking_type: ('daily'|'half_hourly')
 * }} price_info - Price information object
 * @param {Object} t - Translation function
 * @returns {string} Formatted price string
 */
export function format_price_info(price_info, t) {
  const { price, max_price, booking_type: type } = price_info;

  // If price is zero, just show "Free" without period
  if (price === 0) {
    return t('free_of_charge');
  }

  // If price equals max_price, only show max price with its period
  if (max_price && price === max_price) {
    return `${(max_price / 100).toFixed(2)}€${type === 'daily' ? `/${t('month')}` : `/${t('day')}`}`;
  }

  const base = `${(price / 100).toFixed(2)}€`;
  const period = type === 'half_hourly' ? '/30min' 
    : type === 'daily' ? `/${t('day')}`
    : `/${t('month')}`;
  const cap = max_price ? `&nbsp;(max ${(max_price / 100).toFixed(2)}€${type === 'daily' ? `/${t('month')}` : `/${t('day')}`})` : '';
  return `${base}${period}${cap}`;
}

/**
 * Gets the formatted wall-clock time string for a half-hourly slot index.
 * The slot index IS the business-local wall time (slot 20 = 10:00), so the
 * label is derived directly from it — no Date arithmetic. (Adding slot
 * offsets in ms to a midnight timestamp and re-formatting in the business
 * timezone rendered times 1h off on DST transition days.)
 * Honors PUBLIC_TIME_HOUR_FORMAT like generate_time_slots does.
 * Slot 48 (end of the last slot of the day) wraps to "00:00".
 *
 * @param {number} slot - The slot number (0-48, representing 30-minute intervals in a day)
 * @returns {string} Formatted time string (e.g. "13:30", or "1:30 PM" with hour format '12')
 */
export function get_formatted_time(slot) {
  const hours = Math.floor(slot / 2) % 24;
  const minutes = (slot % 2) * 30;
  if (TIME_HOUR_FORMAT === '12') {
    const period = hours < 12 ? 'AM' : 'PM';
    const hours12 = hours % 12 || 12;
    return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
  }
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
};

/**
 * Returns the duration between two half-hourly slot indices as a localized formatted string.
 * Includes both start and end slots in the duration.
 *
 * @param {number} start_slot - Starting slot index
 * @param {number} end_slot - Ending slot index
 * @returns {string} Localized duration string based on TIME_LOCALE
 */
export function format_half_hourly_slot_duration(start_slot, end_slot) {
  const total_minutes = ((end_slot - start_slot) + 1) * 30;
  const hours = Math.floor(total_minutes / 60);
  const minutes = total_minutes % 60;
  
  const rtf = new Intl.RelativeTimeFormat(TIME_LOCALE, { numeric: 'always', style: 'short' });
  
  if (hours && minutes) {
    return `${rtf.format(hours, 'hour')} ${rtf.format(minutes, 'minute')}`.replace(/in |-/g, '');
  }
  if (hours) {
    return rtf.format(hours, 'hour').replace(/in |-/g, '');
  }
  return rtf.format(minutes, 'minute').replace(/in |-/g, '');
}



/**
 * We use this when sending the code to the user via email and when creating the code in the Loxone API (door_locks/+server.js).
 * 
 * Generates a secure 6-digit code for door access based on date, resource, and user.
 * The code is deterministic for the same inputs but includes a salt for security.
 * 
 * @param {string} yyyy_mm_dd - Date in YYYY-MM-DD format
 * @param {number} resource_id - Numeric ID of the resource (e.g., meeting room)
 * @param {number} user_id - Numeric ID of the user
 * @param {string} heartbeat_secret - Secret for generating the code
 * @returns {Promise<string>} A 6-digit code padded with leading zeros
 * @throws {Error} If input parameters are invalid
 */
export async function generate_secure_six_digit_code(yyyy_mm_dd, resource_id, user_id, heartbeat_secret) {
  if (!heartbeat_secret) {
    throw new Error('Heartbeat secret is required for code generation');
  }
  if (!yyyy_mm_dd || !/^\d{4}-\d{2}-\d{2}$/.test(yyyy_mm_dd) || // YYYY-MM-DD
      !Number.isInteger(resource_id) || resource_id <= 0 || // Ensure positive integer
      !Number.isInteger(user_id) || user_id <= 0
    ) {         // Ensure positive integer
      console.error('Invalid input for code generation:', { yyyy_mm_dd, resource_id, user_id });
      throw new Error('Invalid input for code generation');
  }

  // Security: Use HMAC-SHA256 with the secret as key instead of plain SHA-256 + XOR.
  // HMAC is the standard construction for keyed hashing — it ensures the secret is
  // cryptographically bound to the output, not just mixed in via weak XOR.
  // Delimiters prevent ambiguity (e.g. resource_id=1,user_id=23 vs resource_id=12,user_id=3).
  const encoder = new TextEncoder();
  const key = await crypto.subtle.importKey(
    'raw',
    encoder.encode(heartbeat_secret),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  );
  const message = `${yyyy_mm_dd}|${resource_id}|${user_id}`;
  const hash_buffer = await crypto.subtle.sign('HMAC', key, encoder.encode(message));
  const hash_array = new Uint8Array(hash_buffer);
  
  // Use first 4 bytes and ensure positive number with bitwise AND
  const num = ((hash_array[0] << 24) >>> 0) + 
              ((hash_array[1] << 16) >>> 0) + 
              ((hash_array[2] << 8) >>> 0) + 
              (hash_array[3] >>> 0);
  
  // Get 6-digit positive number
  const code = String(num % 1000000).padStart(6, '0');
  return code;
}

/**
 * Validates if a Stripe billing address has the required minimum fields
 */
export function is_stripe_billing_address_valid(address) {
  return address && 
    (address.line1 || address.line2) && 
    address.postal_code && 
    address.city &&
    address.country;
}

/**
 * Resolves the dynamic hostname for the Loxone Miniserver via Remote Connect.
 * Required for HTTPS connections when using Loxone CloudDNS.
 *
 * --------------------------------------------------------------------------------
 *
 * IMPORTANT NOTE: Why this function is necessary:
 * Attempting to connect directly to the Loxone Miniserver using the base CloudDNS URL
 * (e.g., https://dns.loxonecloud.com/SERIAL_NUMBER) for HTTPS API calls
 * (like fetching /data/LoxAPP3.json) resulted in `401 Unauthorized` errors,
 * even with correct Basic Authentication credentials.
 *
 * The Loxone documentation (Communicating with the Miniserver, "What hostname to use with CloudDNS?")
 * clarifies that secure TLS (HTTPS/WSS) connections must go to a dynamic hostname built from the
 * Miniserver's current IP and port (e.g., https://192-168-1-100.SERIAL_NUMBER.dyndns.loxonecloud.com:12345).
 *
 * Connecting directly to the base CloudDNS URL likely causes issues with TLS certificate
 * validation or Host header mismatches on the Miniserver side when accessed remotely via HTTPS,
 * leading to the 401 error ("Processing an encrypted request failed").
 *
 * connect.loxonecloud.com/SERIAL_NUMBER answers with a redirect to exactly that hostname. It
 * replaces the deprecated getip lookup on dns.loxonecloud.com, which since October 2026 waits
 * 10-16 s and then answers Code 482 (Remote Connect timeout) while the Miniserver is reachable.
 *
 * --------------------------------------------------------------------------------
 *
 * An alternative approach would be using websockets with tokens, but this is much more complex.
 *
 * --------------------------------------------------------------------------------
 *
 * @param {string} base_cloud_url - The base CloudDNS URL from env (e.g., https://dns.loxonecloud.com/SERIAL); only the serial number is used.
 * @returns {Promise<string>} The resolved dynamic hostname (e.g., https://cleaned-ip.SERIAL.dyndns.loxonecloud.com:port).
 * @throws {Error} If resolution fails or the response indicates an error.
 */
export async function resolve_loxone_hostname(base_cloud_url) {
  // console.log(`Resolving Loxone hostname from base: ${base_cloud_url}`);
  let serial_number;
  try {
    const url_parts = base_cloud_url.split("/");
    serial_number = url_parts[url_parts.length - 1];
    if (!serial_number || serial_number.length < 10) {
      // Basic validation
      throw new Error("Could not extract serial number.");
    }
    // console.log(`Extracted Serial Number: ${serial_number}`);
  } catch (e) {
    throw new Error(
      `Failed to parse serial number from LOXONE_GATEWAY_HOST_URL '${base_cloud_url}': ${e.message}`
    );
  }

  // HTTPS: the reply decides where the admin credentials are sent next, so it must not be
  // forgeable on the way (connect.loxonecloud.com serves a valid *.loxonecloud.com certificate)
  const lookup_url = `https://connect.loxonecloud.com/${encodeURIComponent(serial_number)}`;

  try {
    // The redirect target is the answer, so it is read instead of followed
    const response = await fetch(lookup_url, {
      redirect: "manual",
      signal: AbortSignal.timeout(10000),
    });
    const location = response.headers.get("location");
    if (response.status < 300 || response.status >= 400 || !location) {
      throw new Error(
        `Remote Connect lookup answered ${response.status} ${response.statusText} without a redirect`
      );
    }

    // Loxone documents the TLS hostname as {cleaned-ip}.{snr}.dyndns.loxonecloud.com; any other
    // host would receive the admin credentials, so it is refused. An unknown or unregistered
    // Miniserver is redirected to a loxone.com landing page and ends up here as well.
    let target;
    try {
      target = new URL(location);
    } catch {
      throw new Error(`Unexpected Remote Connect redirect: ${location}`);
    }
    const host_suffix = `.${serial_number}.dyndns.loxonecloud.com`.toLowerCase();
    const host_ip = target.hostname.endsWith(host_suffix)
      ? target.hostname.slice(0, -host_suffix.length)
      : "";
    // Only hex digits and dashes may end up in the host part
    if (
      target.protocol !== "https:" ||
      target.username ||
      target.password ||
      !/^[0-9a-f-]+$/.test(host_ip)
    ) {
      throw new Error(
        `Remote Connect did not point to the Miniserver (redirect: ${location}); is it registered and online?`
      );
    }

    // console.log(`Successfully resolved Loxone Hostname: ${target.origin}`);
    return target.origin;
  } catch (error) {
    console.error("Error during Loxone hostname resolution:", error);
    throw new Error(`Failed to resolve Loxone hostname: ${error.message}`); // Re-throw to be caught by GET handler
  }
}

/**
 * Comprehensive navigation prevention for kiosk mode
 * Prevents browser back/forward, swipe gestures, keyboard shortcuts, and page unloading
 * 
 * @param {Object} options - Configuration options
 * @param {Function} options.on_activity - Callback function called when user activity is detected
 * @param {boolean} [options.prevent_refresh=true] - Whether to prevent page refresh/reload
 * @param {boolean} [options.prevent_swipes=true] - Whether to prevent swipe navigation
 * @param {boolean} [options.prevent_keyboard=true] - Whether to prevent keyboard shortcuts
 * @returns {Object} Object with cleanup function and methods to control prevention
 * 
 * @example
 * const kiosk_nav = setup_kiosk_navigation_prevention({
 *   on_activity: () => console.log('User activity detected'),
 *   prevent_refresh: true,
 *   prevent_swipes: true,
 *   prevent_keyboard: true
 * });
 * 
 * // Back/forward and links away from the page: register during component init
 * beforeNavigate((navigation) => kiosk_nav?.before_navigate(navigation));
 * 
 * // Temporarily allow navigation for programmatic goto()
 * kiosk_nav.allow_navigation(() => goto('/some-path'));
 * 
 * // Later, cleanup when component unmounts
 * kiosk_nav.cleanup();
 */

export function setup_kiosk_navigation_prevention({ 
  on_activity, 
  prevent_refresh = true, 
  prevent_swipes = true, 
  prevent_keyboard = true
} = {}) {
  const cleanup_functions = [];
  let navigation_temporarily_allowed = false;
  
  // Method 1: Back/forward and link trap, called from SvelteKit's beforeNavigate. A cancelled
  // popstate is undone by SvelteKit itself (history.go back to this entry)
  const before_navigate = (navigation) => {
    if (navigation_temporarily_allowed) return;
    const { from, to } = navigation;
    const leaves_page = !from || !to ||
      to.url.origin !== from.url.origin || to.url.pathname !== from.url.pathname;
    if (navigation.type === 'popstate' || (navigation.type === 'link' && leaves_page)) {
      navigation.cancel();
      on_activity?.();
    }
  };
  
  // Method 2: Swipe prevention
  if (prevent_swipes) {
    // Trackpad swipe prevention
    const handle_wheel = (event) => {
      if (navigation_temporarily_allowed) return;
      if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) {
        event.preventDefault();
        on_activity?.();
      }
    };
    
    // Touch swipe prevention
    let touch_start_x = 0;
    const handle_touchstart = (event) => {
      if (navigation_temporarily_allowed) return;
      touch_start_x = event.touches[0].clientX;
      on_activity?.();
    };
    
    const handle_touchmove = (event) => {
      if (navigation_temporarily_allowed) return;
      const touch_current_x = event.touches[0].clientX;
      const touch_diff = touch_current_x - touch_start_x;
      
      // Prevent swipes from screen edges (navigation gestures)
      if ((touch_start_x < 50 && touch_diff > 50) || 
          (touch_start_x > window.innerWidth - 50 && touch_diff < -50)) {
        event.preventDefault();
      }
      on_activity?.();
    };
    
    window.addEventListener('wheel', handle_wheel, { passive: false });
    window.addEventListener('touchstart', handle_touchstart, { passive: false });
    window.addEventListener('touchmove', handle_touchmove, { passive: false });
    
    cleanup_functions.push(() => {
      window.removeEventListener('wheel', handle_wheel);
      window.removeEventListener('touchstart', handle_touchstart);
      window.removeEventListener('touchmove', handle_touchmove);
    });
  }
  
  // Method 3: Keyboard shortcuts prevention
  if (prevent_keyboard) {
    const handle_keydown = (event) => {
      if (navigation_temporarily_allowed) return;
      const target_element = event.target.nodeName.toLowerCase();
      
      // Prevent backspace navigation (except in input fields)
      if (event.key === 'Backspace' && 
          target_element !== 'input' && 
          target_element !== 'textarea') {
        event.preventDefault();
        event.stopPropagation();
      }
      
      // Prevent common navigation shortcuts
      if ((event.metaKey || event.ctrlKey) && 
          (event.key === 'r' || // Refresh
           event.key === 'R' || // Refresh
           event.key === '[' || // Back
           event.key === ']' || // Forward
           event.key === 'ArrowLeft' || // Back
           event.key === 'ArrowRight')) { // Forward
        event.preventDefault();
      }
      
      // Prevent F5 refresh
      if (event.key === 'F5') {
        event.preventDefault();
      }
      
      on_activity?.();
    };
    
    window.addEventListener('keydown', handle_keydown, { passive: false });
    cleanup_functions.push(() => window.removeEventListener('keydown', handle_keydown));
  }
  
  // Method 4: Page unload prevention
  if (prevent_refresh) {
    const handle_beforeunload = (event) => {
      if (navigation_temporarily_allowed) return;
      event.preventDefault();
      event.returnValue = '';
      return '';
    };
    
    window.addEventListener('beforeunload', handle_beforeunload);
    cleanup_functions.push(() => window.removeEventListener('beforeunload', handle_beforeunload));
  }
  
  // Return control object
  return {
    cleanup: () => {
      cleanup_functions.forEach(cleanup => cleanup());
    },
    before_navigate,
    allow_navigation: async (navigation_callback) => {
      navigation_temporarily_allowed = true;
      try {
        // The callback returns goto's promise, which settles once the navigation is done
        await navigation_callback();
      } finally {
        navigation_temporarily_allowed = false;
      }
    }
  };
}